import React from "react";
import Helmet from "react-helmet";

function WebWorking() {
  return (
    <div className="main-content">
      <Helmet>
        <title>How the Web Works?</title>
        <meta
          name="description"
          content="In this article, we will take a look at the working of the Web and its components. We will also learn about URL."
        />
      </Helmet>
      <main>
        <h1>HOW THE WEB WORKS?</h1>
        <p>
          World Wide Web or simply <b>the Web</b>, is an information system
          where documents and other web resources are shared between clients and
          servers.
        </p>
        <h2>Client and Server</h2>
        <p>
          Clients are the typical web user's internet-connected devices (for
          example, your computer connected to your Wi-Fi, or your phone
          connected to your mobile network) and web-accessing software available
          on those devices (usually a web browser like Firefox or Chrome).
        </p>
        <p>
          Servers are computers that store webpages, sites, or apps. When a
          client device wants to access a webpage, a copy of the webpage is
          downloaded from the server onto the client machine to be displayed in
          the user's web.
        </p>
        <div className="img">
          <img
            src="../images/client-server-model.png"
            alt="Client Server Model"
          />
        </div>
        <h2>So what happens, exactly?</h2>

        <p>When you type a web address into your browser:</p>
        <ul>
          <li>
            The browser goes to the DNS server, and finds out the real address
            of the server that the website lives on.
          </li>
          <li>
            The browser then sends an HTTP request message to that server,
            asking it to send a copy of the website back to the client browser.
            This message, and all other data sent between the client and the
            server, is sent across your internet connection using TCP/IP that
            define how data should travel across the internet.
          </li>
          <li>
            If the server approves the client's request, the server sends the
            client a "200 OK" message, which basically means "Of course you can
            look at that website! Here it is", and then the server starts
            sending the website's files to the browser as a series of small
            chunks called data packets.
          </li>
          <li>
            The client browser assembles the small chunks into a complete web
            page and displays it to you.
          </li>
        </ul>
        <h2>Order in which component files are parsed</h2>
        <p>
          Once the client’s request is approved, the server first sends back the
          HTML (index) file — <b>index.html</b> is commonly named as such, as it
          is the first file of a website to be parsed by the server.
        </p>
        <p>
          The HTML file can reference CSS and JavaScript, either in external
          files via &lt;link&gt; and &lt;script&gt; elements respectively, or
          embedded in the HTML page via style&gt; and &lt;script&gt; elements.
        </p>
        <p>
          From a server standpoint it is important to know the order in which
          these files are parsed when the response is sent back:
        </p>
        <ul>
          <li>
            The HTML file is parsed first and, by looking inside that file, the
            server recognises which CSS and JavaScript files are referenced.
          </li>
          <li>
            After the HTML has been parsed and a DOM tree structure has been
            generated from it, the linked CSS is then parsed, and styles are
            applied to the appropriate parts of the DOM tree. At this point, the
            visual representation of the page is painted to the screen, and the
            user sees the page.
          </li>
          <li>
            The JavaScript usually gets parsed and applied to the page after the
            HTML and CSS.
          </li>
        </ul>
        <h2>Uniform Resource Locator</h2>
        <p>
          Every resource on the internet whether it be an HTML page, a CSS
          document, an image, etc. has an unique address referred to as a
          <b>URL</b>. Examples of URLs are as follows:
        </p>
        <div className="note">
          <p>
            https://en.wikipedia.org
            <br />
            https://en.wikipedia.org/wiki/Main_Page
            <br />
            https://en.wikipedia.org/wiki/search?q=URL
          </p>
        </div>
        <p>
          A URL is composed of different parts, some mandatory and others
          optional.
        </p>
        <div className="img">
          <img src="../images/url-structure.png" alt="URL Structure" />
        </div>
        <ul>
          <li>
            <b>Scheme:</b> The first part of the URL is the scheme, which
            indicates the protocol that the browser must use to request the
            resouce (a protocol is a set method for exchanging or transferring
            data around a computer network). Usually for websites the protocol
            is HTTPS or HTTP (its unsecured version). Addressing web pages
            requires one of these two, but browsers also know how to handle
            other schemes such as mailto: (to open a mail client), so don't be
            surprised if you see other protocols.
          </li>
          <li>
            <b>Authority:</b> Next follows the authority, which is separated
            from the scheme by the character pattern ://. If present the
            authority includes both the domain (e.g. www.example.com) and the
            port (80), separated by a colon:
          </li>
          <ul>
            <li>
              The domain indicates which Web server is being requested. Usually
              this is a domain name, but an IP address may also be used (but
              this is rare as it is much less convenient).
            </li>
            <li>
              The port indicates the technical "gate" used to access the
              resources on the web server. It is usually omitted if the web
              server uses the standard ports of the HTTP protocol (80 for HTTP
              and 443 for HTTPS) to grant access to its resources. Otherwise it
              is mandatory.
            </li>
          </ul>
          <li>
            <b>Path to resource:</b>{" "}
            <span className="highlight">/path/to/myfile.html</span> is the path
            to the resource on the Web server. In the early days of the Web, a
            path like this represented a physical file location on the Web
            server. Nowadays, it is mostly an abstraction handled by Web servers
            without any physical reality.
          </li>
          <li>
            <b>Parameters or query part</b>:
            <span className="highlight">?key1=value1 &amp;key2=value2</span> are
            extra parameters provided to the Web server. Those parameters are a
            list of key/value pairs separated with the &amp; symbol. The Web
            server can use those parameters to do extra stuff before returning
            the resource.
          </li>
          <li>
            <b>Anchor</b>:
            <span className="highlight">#SomewhereInTheDocument</span> is an
            anchor to another part of the resource itself. An anchor represents
            a sort of "bookmark" inside the resource, giving the browser the
            directions to show the content located at that "bookmarked" spot. On
            an HTML document, for example, the browser will scroll to the point
            where the anchor is defined; on a video or audio document, the
            browser will try to go to the time the anchor represents. It is
            worth noting that the part after the #, also known as the fragment
            identifier, is never sent to the server with the request.
          </li>
        </ul>
      </main>
    </div>
  );
}

export default WebWorking;
